import { useTranslation } from "react-i18next";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Skeleton } from "@/shared/components/ui/skeleton";

export interface DataPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
  pageSizeOptions?: number[];
  showInfo?: boolean;
}

export const DataPagination = ({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  pageSizeOptions = [10, 20, 30, 50, 100],
  showInfo = true,
}: DataPaginationProps) => {
  const { t } = useTranslation();

  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const renderPageNumbers = () => {
    if (totalPages === 0) return null;
    
    const pages = [];

    if (currentPage > 2) {
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => onPageChange(1)}>
            1
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (currentPage > 3) {
      pages.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    if (currentPage > 1) {
      pages.push(
        <PaginationItem key={currentPage - 1}>
          <PaginationLink onClick={() => onPageChange(currentPage - 1)}>
            {currentPage - 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key={currentPage}>
        <PaginationLink isActive>
          {currentPage}
        </PaginationLink>
      </PaginationItem>
    );

    if (currentPage < totalPages) {
      pages.push(
        <PaginationItem key={currentPage + 1}>
          <PaginationLink onClick={() => onPageChange(currentPage + 1)}>
            {currentPage + 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (currentPage < totalPages - 2) {
      pages.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    if (currentPage < totalPages - 1) {
      pages.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => onPageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 mt-4">
      {showInfo && (
        <div className="text-sm text-muted-foreground w-full sm:w-auto">
          {isLoading ? (
            <Skeleton className="h-4 w-[250px]" />
          ) : (
            <span>
              {t('common.pagination.showing', {
                start: startItem,
                end: endItem,
                total: totalItems,
                defaultValue: `Showing ${startItem} to ${endItem} of ${totalItems} items`
              })}
            </span>
          )}
        </div>
      )}

      <div className="flex justify-center items-center w-full sm:w-auto">
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                {currentPage > 1 ? (
                  <PaginationPrevious
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  />
                ) : (
                  <span className="opacity-50">
                    <PaginationPrevious />
                  </span>
                )}
              </PaginationItem>

              {renderPageNumbers()}

              <PaginationItem>
                {currentPage < totalPages ? (
                  <PaginationNext
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  />
                ) : (
                  <span className="opacity-50">
                    <PaginationNext />
                  </span>
                )}
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end w-full sm:w-auto">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {t('common.pagination.itemsPerPage', { defaultValue: 'Items per page' })}
        </span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(parseInt(value))}
        >
          <SelectTrigger className="w-[70px]">
            <SelectValue placeholder={pageSize.toString()} />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
